import React, { useEffect, useState } from "react";
import { MapPin, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getCurrentCoords } from "../lib/geo";

const DISMISS_KEY = "cm_geo_primer_dismissed";

// Aviso amigável (1ª vez) pedindo permissão de localização ao colaborador,
// para que o "Dar andamento" / "Cheguei na casa" funcione sem falhas de GPS.
export function LocationPermissionPrimer() {
  const [show, setShow] = useState(false);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!("geolocation" in navigator)) return;
      // Já concedida? Não mostra.
      try {
        if (navigator.permissions?.query) {
          const st = await navigator.permissions.query({ name: "geolocation" });
          if (!active) return;
          if (st.state === "granted") return;
          if (st.state === "denied") {
            setDenied(true);
            setShow(true);
            return;
          }
        }
      } catch (e) {
        // Permissions API indisponível — segue para o fallback abaixo.
      }
      // Estado "prompt" ou desconhecido: mostra só se ainda não foi dispensado.
      if (active && localStorage.getItem(DISMISS_KEY) !== "1") setShow(true);
    })();
    return () => {
      active = false;
    };
  }, []);

  async function allow() {
    setBusy(true);
    try {
      await getCurrentCoords();
      toast.success("Localização ativada! Agora você pode iniciar as limpezas.");
      localStorage.setItem(DISMISS_KEY, "1");
      setShow(false);
    } catch (e) {
      setDenied(true);
      toast.error(e?.message || "Não foi possível ativar a localização.");
    } finally {
      setBusy(false);
    }
  }

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  }

  if (!show) return null;

  return (
    <div className="px-4 pt-3" data-testid="geo-permission-primer">
      <div className="relative rounded-2xl border border-blue-200 bg-blue-50 p-4 shadow-sm">
        <button
          onClick={dismiss}
          data-testid="geo-primer-dismiss"
          className="absolute top-3 right-3 text-blue-400 hover:text-blue-600"
          aria-label="Fechar"
        >
          <X size={18} />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
            <MapPin size={20} />
          </div>
          <div className="min-w-0">
            <div className="font-bold text-blue-900 text-base">
              Ative sua localização
            </div>
            <p className="text-sm text-blue-800 mt-0.5">
              {denied
                ? "A localização está bloqueada. Abra as configurações do navegador, permita o acesso à localização para este site e recarregue a página."
                : "Para registrar o início das limpezas no local correto, permita o acesso à sua localização. Toque no botão e escolha “Permitir”."}
            </p>
            {!denied && (
              <button
                onClick={allow}
                disabled={busy}
                data-testid="geo-primer-allow"
                className="mt-3 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {busy ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Ativando…
                  </>
                ) : (
                  <>
                    <MapPin size={16} />
                    Permitir localização
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
